import { useCourses } from 'core/hooks/course/useCourses';
import { useGroups } from 'core/hooks/useGroups';
import * as coreEntities from 'core/store/index';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { createAnalyticsService } from '../services';

/**
 * Hook to fetch AI-eligible discipline IDs from the API
 * @returns {{ disciplineIds: string[], isLoaded: boolean }}
 */
const useAIEligibleDisciplines = () => {
  const [disciplineIds, setDisciplineIds] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const fetchDisciplines = async () => {
      try {
        const service = createAnalyticsService();
        const response = await service.getEligibleSubjects();

        if (response?.data && Array.isArray(response.data)) {
          const ids = response.data.flatMap((subject) => subject.disciplines?.map((d) => d.id) || []);
          setDisciplineIds(ids);
        }
      } catch (error) {
        console.error('[useAIEligibleDisciplines] Failed to fetch eligible disciplines:', error);
      } finally {
        setIsLoaded(true);
      }
    };

    fetchDisciplines();
  }, []);

  return { disciplineIds, isLoaded };
};

/**
 * Hook to fetch diagnostic report for a specific course
 * @param {string|null} courseGuid - The course GUID to fetch the report for
 * @returns {{ report: object|null, loading: boolean }}
 */
const useAnalyticsReport = (courseGuid) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!courseGuid) {
      setReport(null);
      return;
    }

    let isMounted = true;

    const fetchReport = async () => {
      setLoading(true);
      try {
        const service = createAnalyticsService();
        const data = await service.getClassDiagnosticReport(courseGuid);
        if (isMounted) {
          const hasData = data?.unitMetrics?.length > 0;
          setReport(hasData ? data : null);
        }
      } catch (error) {
        console.error(`[useAnalyticsReport] Failed to fetch analytics for course ${courseGuid}:`, error);
        if (isMounted) setReport(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchReport();
    return () => {
      isMounted = false;
    };
  }, [courseGuid]);

  return { report, loading };
};

/**
 * Main hook for managing teacher analytics data
 * Orchestrates data fetching and provides derived state for the analytics view
 */
const useTeacherAnalytics = () => {
  const { classId } = useParams();
  const history = useHistory();

  // External data sources
  const { groups } = useGroups();
  const { courses } = useCourses();
  const coursesLoading = useSelector((state) => coreEntities.courses.selectors.isLoading(state));
  const coursesForClass = useSelector((state) => coreEntities.courses.selectors.getCoursesOfGroup(state, classId));

  // Local state
  const [selectedCourse, setSelectedCourse] = useState(null);

  // API hooks
  const { disciplineIds, isLoaded: disciplinesLoaded } = useAIEligibleDisciplines();
  const { report, loading: reportLoading } = useAnalyticsReport(selectedCourse);

  // Derived: is all prerequisite data ready?
  const isDataReady = disciplinesLoaded && !coursesLoading && !!courses && !!groups?.schoolGroups;

  // Derived: eligible courses for current class
  const eligibleCourses = useMemo(() => {
    if (!coursesForClass || disciplineIds.length === 0) {
      return [];
    }

    const filtered = coursesForClass
      .filter((c) => c.has_tangerine_ai && disciplineIds.includes(c.education_discipline_guid))
      .map((c) => ({
        ...c,
        disciplineGuid: c.education_discipline_guid,
        disciplineName: c.education_discipline_name,
      }));
    return filtered;
  }, [coursesForClass, disciplineIds]);

  // Derived: groups that have at least one eligible course
  const eligibleGroups = useMemo(() => {
    if (!groups?.schoolGroups || !courses || disciplineIds.length === 0) {
      return [];
    }

    const filtered = groups.schoolGroups.filter((group) => {
      const groupCourses = courses.filter((c) => c.school_group_guid === group.guid);
      return groupCourses.some((c) => c.has_tangerine_ai && disciplineIds.includes(c.education_discipline_guid));
    });
    return filtered;
  }, [groups?.schoolGroups, courses, disciplineIds]);

  // Derived: recommendations from report
  const recommendations = useMemo(() => {
    if (!report?.unitMetrics) return [];
    return report.unitMetrics.map((unit) => ({
      unitId: unit.unitId,
      title: unit.title,
      order: unit.order,
      lessons:
        unit.lessonMetrics
          ?.filter((l) => l.hasAlerts)
          .map((l) => ({
            lessonId: l.lessonId,
            title: l.title,
            order: l.order,
            alertReasons: l.alertReasons || [],
          })) || [],
      hasAlerts: unit.lessonMetrics?.some((l) => l.hasAlerts) || false,
    }));
  }, [report]);

  // Effect: redirect to first eligible group if no classId
  useEffect(() => {
    if (!isDataReady) {
      return;
    }
    if (!classId && eligibleGroups.length > 0) {
      history.replace(`/analytics/${eligibleGroups[0].guid}`);
    }
  }, [isDataReady, classId, eligibleGroups, history]);

  // Effect: auto-select first course when data is ready
  useEffect(() => {
    if (isDataReady && !selectedCourse && eligibleCourses.length > 0) {
      setSelectedCourse(eligibleCourses[0].guid);
    }
  }, [isDataReady, selectedCourse, eligibleCourses]);

  // Derived: overall loading state
  const loading = !isDataReady || reportLoading;

  // Derived: current report with course metadata
  const currentReport = report
    ? {
        courseId: selectedCourse,
        courseName: eligibleCourses.find((c) => c.guid === selectedCourse)?.name,
        disciplineGuid: eligibleCourses.find((c) => c.guid === selectedCourse)?.disciplineGuid,
        disciplineName: eligibleCourses.find((c) => c.guid === selectedCourse)?.disciplineName,
        report,
      }
    : null;

  return {
    loading,
    eligibleCourses,
    groups: eligibleGroups,
    currentGroup: eligibleGroups.find((g) => g.guid === classId) || null,
    currentReport,
    recommendations,
    selectedCourse,
    setSelectedCourse,
    classId,
    className: coursesForClass?.[0]?.school_group_name || '',
    hasMultipleCourses: eligibleCourses.length > 1,
  };
};

export default useTeacherAnalytics;
